import { ArticleServiceType } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import { WorkflowPublic } from '@/models/workflow';

import { getArticleIam } from '../iam';
import { type RequestArticleContextData } from '../interfaces';

export const computeServiceChoiceOptions = (
  article: ArticleWithRelations,
  view: RequestArticleContextData['view'],
  stepName: 'creation' | 'service-choice' | 'analysis' | 'processing',
  workflow?: WorkflowPublic
) => {
  let serviceOptions: ArticleServiceType[] | undefined = [];

  switch (stepName) {
    case 'creation':
      serviceOptions =
        !!article.step?.config &&
        !!('requireServiceChoice' in article.step.config) &&
        !!article.step.config.requireServiceChoice
          ? article.step.config.requireServiceChoice.serviceOptions
          : undefined;

      break;

    case 'service-choice':
      serviceOptions =
        !!article.step?.config && 'serviceOptions' in article.step.config
          ? article.step.config.serviceOptions
          : undefined;

      break;

    case 'analysis': {
      const analysisServiceOptions =
        (!!article.step?.config && 'serviceOptions' in article.step.config
          ? article.step.config.serviceOptions
          : undefined) ?? [];

      serviceOptions = getFilteredServiceOptionsForWorkshop(article, analysisServiceOptions);

      break;
    }

    case 'processing': {
      const analysisNode = workflow?.nodes.find(
        (node) => 'step' in node && node.step === 'analysis'
      );
      const analysisServiceOptions =
        (!!analysisNode?.config && 'serviceOptions' in analysisNode.config
          ? analysisNode.config.serviceOptions
          : undefined) ?? [];

      serviceOptions = getFilteredServiceOptionsForWorkshop(article, analysisServiceOptions);

      break;
    }

    default:
      return [];
  }

  const _isArticleEligibleToExchange = isArticleEligibleToExchange(article, view, stepName);

  if (!_isArticleEligibleToExchange) {
    return serviceOptions?.filter((serviceOption) => serviceOption !== 'exchange');
  }

  return serviceOptions;
};

const getFilteredServiceOptionsForWorkshop = (
  article: ArticleWithRelations,
  taskServiceOptions: ArticleServiceType[]
): ArticleServiceType[] => {
  const workshopServiceOptions = article.workshopOrganization?.workshopConfig?.serviceOptions || [];

  return taskServiceOptions.filter((option) => workshopServiceOptions.includes(option));
};

const isArticleEligibleToExchange = (
  article: ArticleWithRelations,
  view: RequestArticleContextData['view'],
  stepName: 'creation' | 'service-choice' | 'analysis' | 'processing'
) => {
  const { hasUserScopeOnArticle } = getArticleIam(article);

  const shouldApplyWarrantyAndBrandResponsibilityRules =
    stepName === 'creation' || stepName === 'service-choice';
  const isWarrantyEnabled = view.warranty.shown && view.warranty.type === 'warranty';

  if (isWarrantyEnabled && shouldApplyWarrantyAndBrandResponsibilityRules) {
    const isArticleUnderWarranty = !!article.activeWarrantyType;

    if (!isArticleUnderWarranty) {
      return false;
    }

    const hasAtLeastOneDefectUnderBrandResponsibility = article.snapshot.articleDefects.some(
      (defect) => !!defect.brandResponsibility
    );

    if (!hasAtLeastOneDefectUnderBrandResponsibility) {
      return false;
    }
  }

  const isProductExchangeEnabled =
    !article.product?.exchangeEligibility || // No config = true by default
    article.product.exchangeEligibility.enabled;

  if (!isProductExchangeEnabled) {
    return false;
  }

  const isActingOnBehalfOfWorkshop =
    article.taskType === 'analyze_article' ||
    article.taskType === 'adjust_service_during_processing';

  const isProductExchangeEligibleToTraits =
    !article.product?.exchangeEligibility?.traits?.length || // No traits = available to all traits
    article.product.exchangeEligibility.traits.some(
      (productExchangeTrait) =>
        hasUserScopeOnArticle(productExchangeTrait) ||
        // Handle "workshop" trait when acting on behalf of the workshop
        (productExchangeTrait === 'workshop' && isActingOnBehalfOfWorkshop)
    );

  if (!isProductExchangeEligibleToTraits) {
    return false;
  }

  return true;
};
